import { useTranslation } from "react-i18next";
import { faCheck, faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    Progress,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Widget,
    WidgetActions,
    WidgetContent,
    WidgetHeader,
    WidgetTitle,
} from "frontend-components";
import { useMostVisitedPages, type PageData } from "@dashboard/hooks/useMostVisitedPages";

function ChangePercent({ value }: { value: number | null }) {
    if (value == null) {
        return <span className="text-xs text-compendia-muted">—</span>;
    }

    const rounded = Math.round(value);
    const isNegative = rounded < 0;
    const label = isNegative ? `−${Math.abs(rounded)}%` : `+${rounded}%`;

    return (
        <span
            className={`text-xs font-semibold ${
                isNegative ? "text-chameleon-pink" : "text-compendia-gray-80"
            }`}
        >
            {label}
        </span>
    );
}

export function MostVisitedPagesWidget() {
    const { t, i18n } = useTranslation();
    const locale = i18n.language || "nb";

    const {
        pages,
        products,
        totalViews,
        periodLabel,
        isLoading,
        isError,
        useMockData,
        missingAnalyticsKey,
        selectedProductUrl,
        setSelectedProductUrl,
    } = useMostVisitedPages();

    const selectedProductName = selectedProductUrl
        ? products.find((p) => p.url === selectedProductUrl)?.name
        : t("dashboard.allProducts");

    const hasData = pages.length > 0;
    const maxViews = hasData ? pages[0].views : 0;
    const showStaleNotice = hasData && isError;

    const showProductSelector = products.length > 1 && hasData;

    return (
        <Widget>
            <WidgetHeader>
                <div className="flex min-w-0 flex-col">
                    <WidgetTitle>{t("common.mostVisitedPages")}</WidgetTitle>
                    {hasData && (
                        <p className="text-xs text-compendia-muted mt-0.5">
                            {totalViews.toLocaleString(locale)}{" "}
                            {t("dashboard.totalViews").toLowerCase()} · {periodLabel}
                        </p>
                    )}
                </div>
                {showProductSelector && (
                    <WidgetActions>
                        <DropdownMenu>
                            <DropdownMenuTrigger
                                className="group inline-flex items-center gap-2 rounded-full border border-compendia-border bg-compendia-card px-3.5 py-1.5 text-label font-medium text-compendia-gray-80 hover:bg-compendia-greige-10 transition-colors whitespace-nowrap"
                                aria-label={t("dashboard.selectProduct")}
                            >
                                <span>{selectedProductName}</span>
                                <FontAwesomeIcon
                                    icon={faChevronDown}
                                    className="size-3 text-compendia-gray-80 transition-transform duration-150 group-data-popup-open:rotate-180"
                                />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="max-h-60 overflow-y-auto min-w-44"
                            >
                                <DropdownMenuItem
                                    className="justify-between text-label text-compendia-gray-80"
                                    onClick={() => setSelectedProductUrl(null)}
                                >
                                    <span
                                        className={
                                            !selectedProductUrl ? "font-semibold" : undefined
                                        }
                                    >
                                        {t("dashboard.allProducts")}
                                    </span>
                                    {!selectedProductUrl && (
                                        <FontAwesomeIcon icon={faCheck} className="size-3" />
                                    )}
                                </DropdownMenuItem>
                                {products.map((p) => {
                                    const isSelected = selectedProductUrl === p.url;
                                    return (
                                        <DropdownMenuItem
                                            key={p.id}
                                            className="justify-between text-label text-compendia-gray-80"
                                            onClick={() => setSelectedProductUrl(p.url)}
                                        >
                                            <span
                                                className={isSelected ? "font-semibold" : undefined}
                                            >
                                                {p.name}
                                            </span>
                                            {isSelected && (
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    className="size-3"
                                                />
                                            )}
                                        </DropdownMenuItem>
                                    );
                                })}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </WidgetActions>
                )}
            </WidgetHeader>

            <WidgetContent className="gap-0 overflow-y-auto p-0">
                {!hasData && missingAnalyticsKey ? (
                    <p className="text-label text-compendia-muted px-5 py-4">
                        {t("dashboard.noAnalyticsData")}
                    </p>
                ) : !hasData && isError ? (
                    <p className="text-chameleon-pink text-label px-5 py-4">
                        {t("common.errorLoadingData")}
                    </p>
                ) : (
                    <>
                        {useMockData && (
                            <p className="text-xs text-compendia-muted bg-compendia-greige-10 border-b border-compendia-border px-5 py-2">
                                DEV: {t("dashboard.mockDataNotice")}
                            </p>
                        )}
                        {showStaleNotice && (
                            <p className="text-xs text-compendia-muted bg-compendia-greige-10 border-b border-compendia-border px-5 py-2">
                                {t("dashboard.staleDataNotice")}
                            </p>
                        )}

                        <Table className="w-full border-collapse table-fixed">
                            <colgroup>
                                <col className="w-12" />
                                <col />
                                <col className="w-24" />
                                <col className="w-24" />
                            </colgroup>
                            <TableHeader className="bg-compendia-greige-10 sticky top-0 z-10">
                                <TableRow className="border-b border-compendia-border hover:bg-transparent">
                                    <TableHead className="px-5 py-2.5 text-category tracking-category uppercase font-semibold text-compendia-muted">
                                        #
                                    </TableHead>
                                    <TableHead className="px-2 py-2.5 text-category tracking-category uppercase font-semibold text-compendia-muted">
                                        {t("common.page")}
                                    </TableHead>
                                    <TableHead className="px-2 py-2.5 text-right text-category tracking-category uppercase font-semibold text-compendia-muted">
                                        {t("dashboard.views")}
                                    </TableHead>
                                    <TableHead className="px-5 py-2.5 text-right text-category tracking-category uppercase font-semibold text-compendia-muted">
                                        {t("dashboard.growth")}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    Array.from({ length: 6 }).map((_, idx) => (
                                        <TableRow
                                            key={`skeleton-${idx}`}
                                            className="border-b border-compendia-border last:border-b-0 hover:bg-transparent"
                                        >
                                            <TableCell className="px-5 py-3 align-middle">
                                                <Skeleton className="h-3 w-4 rounded" />
                                            </TableCell>
                                            <TableCell className="px-2 py-3 align-middle">
                                                <Skeleton className="h-3 w-3/4 rounded" />
                                                <Skeleton className="mt-1.5 h-1 w-full rounded-full" />
                                            </TableCell>
                                            <TableCell className="px-2 py-3 align-middle">
                                                <Skeleton className="ml-auto h-3 w-12 rounded" />
                                            </TableCell>
                                            <TableCell className="px-5 py-3 align-middle">
                                                <Skeleton className="ml-auto h-3 w-10 rounded" />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : pages.length === 0 ? (
                                    <TableRow className="hover:bg-transparent">
                                        <TableCell
                                            colSpan={4}
                                            className="px-5 py-10 text-center text-label text-compendia-muted"
                                        >
                                            {t("dashboard.noAnalyticsData")}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    pages.map((page: PageData, idx) => {
                                        const barPercent =
                                            maxViews > 0 ? (page.views / maxViews) * 100 : 0;
                                        return (
                                            <TableRow
                                                key={page.pagePath}
                                                className="border-b border-compendia-border last:border-b-0 hover:bg-compendia-greige-10 transition-colors"
                                            >
                                                <TableCell className="px-5 py-3 align-middle text-xs font-medium text-compendia-muted">
                                                    {idx + 1}
                                                </TableCell>
                                                <TableCell className="px-2 py-3 align-middle">
                                                    <a
                                                        href={page.externalUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="block truncate text-label font-medium text-compendia-gray-80 hover:underline"
                                                        title={page.pageName}
                                                    >
                                                        {page.pageName}
                                                    </a>
                                                    <Progress
                                                        value={barPercent}
                                                        color="green"
                                                        className="mt-1.5"
                                                        aria-label={t("dashboard.views")}
                                                    />
                                                </TableCell>
                                                <TableCell className="px-2 py-3 align-middle text-right text-label tabular-nums text-compendia-gray-80">
                                                    {page.views.toLocaleString(locale)}
                                                </TableCell>
                                                <TableCell className="px-5 py-3 align-middle text-right">
                                                    <ChangePercent value={page.changePercent} />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </>
                )}
            </WidgetContent>
        </Widget>
    );
}
